import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Chip,
  enqueueSnackbar,
  InputContainer,
  Paper,
  SearchableSelect,
  Typography,
  YardPlaceScene,
  YardPlaceSceneContainerPlace,
  YardPlaceSelects,
} from '../../design-system';
import type { ContainerType } from '../../types';
import { DashboardAutoColumnInputs, DashboardHeader } from '../dashboard';
import {
  useEditContainerMutation,
  useIsSmallScreen,
  useQuery,
  useRemovePlaceContainerMutation,
  useYardBlocksSceneQuery,
} from '../../hooks';
import { containersService } from '../../services';
import {
  extendContainerValidator,
  formatContainerType,
  momentDiffDays,
  momentTextFormat,
} from '../../utils';
import {
  ContainerConditionToChipColors,
  ContainerConditionToChipLabels,
  ContainerStatusToChipColors,
  ContainerStatusToChipLabels,
  MESSAGES,
  TABLE,
} from '../../const';
import {
  validateContainerPlace,
  parseSceneToSlotsState,
  getNextContainerTier,
  getContainerPlaceFromBlocksScene,
  CONTAINER_PLACE_API_TIER,
} from '../../design-system/yard-place-scene/validate-container-place';
import { buildContainerSceneKey } from '../../design-system/yard-place-scene/place-utils';
import { useStore } from '../../context';

const SetLocationPage = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr;
  height: 100%;

  & > *:first-child {
    padding: var(--spacing-3);
  }

  & > *:nth-child(2) > *:first-child {
    border-radius: 0;
  }
`;

const SetLocationPageMobile = styled(SetLocationPage)`
  grid-template-rows: max-content 1fr max-content;
`;

const MobileLocationFooter = styled.div`
  display: grid;
  gap: var(--spacing-3);
  padding: var(--spacing-3);
  background: var(--white);
  border-top: 1px solid var(--gray-40);
`;

const removeContainerFromScene = (blocksScene: string, containerId: string) => {
  try {
    const parsed = JSON.parse(blocksScene || '{}');

    if (!Array.isArray(parsed.containers)) return blocksScene;

    return JSON.stringify({
      ...parsed,
      containers: parsed.containers.filter(
        (container: { id: string }) => container.id !== containerId,
      ),
    });
  } catch {
    return blocksScene;
  }
};

const updateContainerPlaceInScene = (
  blocksScene: string,
  container: {
    id: string;
    number: string;
    block: string;
    row: string;
    slot: string;
    tier: string;
    length?: number;
  },
) => {
  try {
    const parsed = JSON.parse(blocksScene || '{}');
    const slotsState = parseSceneToSlotsState(blocksScene);
    const key = buildContainerSceneKey(
      slotsState,
      container.block,
      container.row,
      container.slot,
      container.length,
    );
    const tier = Number(container.tier) || 1;

    if (!Array.isArray(parsed.containers)) {
      parsed.containers = [];
    }

    const nextContainer = {
      id: container.id,
      number: container.number,
      key,
      tier,
      ...(container.length ? { length: container.length } : {}),
    };
    const existingIndex = parsed.containers.findIndex(
      (item: { id: string }) => item.id === container.id,
    );

    parsed.containers =
      existingIndex >= 0
        ? parsed.containers.map((item: { id: string }, index: number) =>
            index === existingIndex ? { ...item, ...nextContainer } : item,
          )
        : [...parsed.containers, nextContainer];

    return JSON.stringify(parsed);
  } catch {
    return blocksScene;
  }
};

const MobileLocationActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-3);
`;

const HOVER_MENU_OFFSET = 12;

const SceneHoverInfoPaper = styled(Paper)`
  display: grid;
  gap: var(--spacing-2);
  width: max-content;
  height: auto;
  padding: var(--spacing-2) !important;
`;

const SceneHoverInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-6);
  min-height: 3.2rem;
  padding: var(--spacing-2) var(--spacing-3);
  border-radius: 0.4rem;
  background: var(--gray-30);
`;

type SceneHoverMenuItem = {
  label: string;
  value: string | number;
};

type SceneHoverMenuProps = {
  items: SceneHoverMenuItem[];
  x: number;
  y: number;
};

const SceneHoverMenu = ({ items, x, y }: SceneHoverMenuProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const parent = wrapper?.offsetParent as HTMLElement | null;
    if (!wrapper || !parent) return;

    const { offsetWidth: width, offsetHeight: height } = wrapper;
    let translateX = HOVER_MENU_OFFSET;
    let translateY = HOVER_MENU_OFFSET;

    if (x + HOVER_MENU_OFFSET + width > parent.clientWidth) {
      translateX = -(width + HOVER_MENU_OFFSET);
    }

    if (y + HOVER_MENU_OFFSET + height > parent.clientHeight) {
      translateY = -(height + HOVER_MENU_OFFSET);
    }

    wrapper.style.transform = `translate(${translateX}px, ${translateY}px)`;
  }, [items, x, y]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        zIndex: 2,
        pointerEvents: 'none',
        transform: `translate(${HOVER_MENU_OFFSET}px, ${HOVER_MENU_OFFSET}px)`,
      }}
    >
      <SceneHoverInfoPaper paperSize="max-content">
        {items.map((item) => (
          <SceneHoverInfoRow key={item.label}>
            <Typography variant="textS" weight="medium">
              {item.label}
            </Typography>
            <Typography variant="textS">{item.value}</Typography>
          </SceneHoverInfoRow>
        ))}
      </SceneHoverInfoPaper>
    </div>
  );
};

const SlotHoverInfo = ({ place, tier, x, y }: { place: string; tier: number; x: number; y: number }) => (
  <SceneHoverMenu
    x={x}
    y={y}
    items={[
      { label: 'Place', value: place },
      { label: 'Tier', value: tier },
    ]}
  />
);

const ContainerHoverInfo = ({
  number,
  type,
  clientCode,
  place,
  tier,
  x,
  y,
}: {
  number: string;
  type?: ContainerType;
  clientCode?: string;
  place: string;
  tier: number;
  x: number;
  y: number;
}) => (
  <SceneHoverMenu
    x={x}
    y={y}
    items={[
      { label: 'Container Number', value: number },
      { label: 'Type', value: type ? formatContainerType(type) : '—' },
      { label: 'Client', value: clientCode || '—' },
      { label: 'Place', value: place },
      { label: 'Tier', value: tier },
    ]}
  />
);

type MobilePlace = {
  block: string;
  row: string;
  slot: string;
  tier: string;
};

type MobileSetLocationPanelProps = {
  yardId: string;
  blocksScene?: string;
  selectedContainerId: string;
  containerLength?: number;
  containerNumber: string;
  block: string;
  row: string;
  slot: string;
  tier: string;
  initialPlace: MobilePlace;
  setBlock: (block: string) => void;
  setRow: (row: string) => void;
  setSlot: (slot: string) => void;
  setTier: (tier: string) => void;
  onContainerSelect: (option: any) => void;
  onSave: () => void;
  onDelete: () => void;
};

const MobileSetLocationPanel = ({
  yardId,
  blocksScene,
  selectedContainerId,
  containerLength,
  containerNumber,
  block,
  row,
  slot,
  tier,
  initialPlace,
  setBlock,
  setRow,
  setSlot,
  setTier,
  onContainerSelect,
  onSave,
  onDelete,
}: MobileSetLocationPanelProps) => {
  const hasLocation = !!(initialPlace.block && initialPlace.row && initialPlace.slot);
  const isPlaceValid = !!(block && row && slot);
  const isPlaceChanged =
    block !== initialPlace.block || row !== initialPlace.row || slot !== initialPlace.slot;
  const canSave = !!selectedContainerId && isPlaceValid && isPlaceChanged;

  if (!yardId) return null;

  return (
    <MobileLocationFooter>
      <SearchableSelect
        InputComponent={InputContainer}
        label={'Container Number'}
        selectedOptionLabel={containerNumber || 'Select Container Number'}
        queryKey={['container-short', yardId, 'set-location-mobile']}
        queryFn={(number: string) =>
          containersService.options({
            yardId,
            clientYardOptional: true,
            number,
            take: TABLE.PAGE_MAX_SIZE,
            skip: 0,
            short: true,
            isQuery: true,
            notLoading: true,
            allowHold: true,
            hasLocation: 'all',
          })
        }
        selectedAreasEqaul={{ key: 'id', keys: selectedContainerId ? [selectedContainerId] : [] }}
        onSelect={onContainerSelect}
      />
      <DashboardAutoColumnInputs>
        <YardPlaceSelects
          yardId={yardId}
          blocksScene={blocksScene}
          containerId={selectedContainerId}
          containerLength={containerLength}
          block={block}
          row={row}
          slot={slot}
          tier={tier}
          setBlock={setBlock}
          setRow={setRow}
          setSlot={setSlot}
          setTier={setTier}
          disabledTier
          disabled={!containerNumber}
          disabledRequest
          withDefaultValues
          isSubmitClicked={isPlaceChanged}
          hideHelperText
        />
      </DashboardAutoColumnInputs>
      <MobileLocationActions>
        <Button
          size="medium"
          data-color="destructive"
          disabled={!selectedContainerId || !hasLocation}
          onClick={onDelete}
        >
          Delete
        </Button>
        <Button size="medium" disabled={!canSave} onClick={onSave}>
          Save
        </Button>
      </MobileLocationActions>
    </MobileLocationFooter>
  );
};

const EmptySlotContainerPaper = styled(Paper)`
  position: absolute;
  top: var(--spacing-4);
  left: var(--spacing-4);
  z-index: 1;

  display: grid;
  grid-template-columns: 25rem;
  grid-auto-flow: row;
  gap: var(--spacing-6);
  align-items: end;
  width: max-content;
  height: auto;

  border-radius: var(--border-radius-6);
`;

type EmptySlotContainerSelectProps = {
  yardId: string;
  hasSelectedSlot?: boolean;
  sceneContainers?: { id: string; number: string }[];
  onAddContainer?: (container: { id: string; number: string; length: number }) => void;
  onSceneContainerSelect?: (container: { id: string; number: string }) => void;
  onClearSceneContainerSelect?: () => void;
};

const EmptySlotContainerSelect = ({
  yardId,
  hasSelectedSlot = false,
  sceneContainers = [],
  onAddContainer,
  onSceneContainerSelect,
  onClearSceneContainerSelect,
}: EmptySlotContainerSelectProps) => {
  const [selectedContainer, setSelectedContainer] = useState<{
    id: string;
    number: string;
    length: number;
  }>();

  if (!yardId) return null;

  const isSelectedInScene =
    !!selectedContainer && sceneContainers.some((item) => item.id === selectedContainer.id);

  return (
    <EmptySlotContainerPaper paperSize="max-content">
      <SearchableSelect
        InputComponent={InputContainer}
        label={'Container Number'}
        selectedOptionLabel={selectedContainer?.number || 'Select Container Number'}
        queryKey={['container-short', yardId, 'set-location']}
        queryFn={(number: string) => async () => {
          const params: any = {
            yardId,
            number,
            take: TABLE.PAGE_MAX_SIZE,
            skip: 0,
            short: true,
            hasLocation: 'all',
            notLoading: true,
          };

          if (
            typeof params.number === 'string' &&
            (params.number?.length < 3 || !extendContainerValidator(params.number))
          )
            delete params.number;

          const { data } = await containersService.getAll({
            params,
          });

          return data.data.map((container: any) => ({
            id: container.id,
            label: container.number,
            length: container.type.startsWith('20') ? 20 : 40,
          }));
        }}
        selectedAreasEqaul={{ key: 'id', keys: selectedContainer ? [selectedContainer.id] : [] }}
        onSelect={(container) => {
          const nextContainer = container
            ? {
                id: container.id,
                number: container.label,
                length: container.length,
              }
            : undefined;

          setSelectedContainer(nextContainer);

          if (!nextContainer) {
            onClearSceneContainerSelect?.();
            return;
          }

          const existsInScene = sceneContainers.some((item) => item.id === nextContainer.id);

          if (existsInScene) {
            onSceneContainerSelect?.({ id: nextContainer.id, number: nextContainer.number });
            return;
          }

          onClearSceneContainerSelect?.();
        }}
      />
      <Button
        size="medium"
        disabled={!selectedContainer || !hasSelectedSlot || isSelectedInScene}
        onClick={() => selectedContainer && onAddContainer?.(selectedContainer)}
      >
        Save
      </Button>
    </EmptySlotContainerPaper>
  );
};

const ContainerDetailsPaper = styled(EmptySlotContainerPaper)`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-3);
  align-items: stretch;
`;

const ContainerDetailsHeader = styled.div`
  display: grid;
  gap: var(--spacing-1);
`;

const ContainerDetail = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-6);
  min-height: 3.2rem;
  padding: var(--spacing-2) var(--spacing-3);
  border-radius: 0.4rem;
  background: var(--gray-30);
`;

type ContainerDetailsProps = {
  containerId: string;
  onClose: () => void;
  onDelete: () => void;
};

const ContainerDetails = ({ containerId, onClose, onDelete }: ContainerDetailsProps) => {
  const { data: container, isFetching } = useQuery(
    ['container', containerId],
    () =>
      containersService
        .getAll({
          params: { containerIds: [containerId] },
        })
        .then((res) => res.data.data[0]),
    { enabled: !!containerId },
  );

  if (isFetching || !container?.id) return null;

  const marketStatus = container.isHold
    ? 'HOLD'
    : container.sale?.status
    ? container.sale.status
    : undefined;
  const status =
    container.status === 'GATE_OUT_REQUESTED' && container.gateOut?.status === 'EXPIRED'
      ? 'GATE_OUT_EXPIRED'
      : container.status;
  const condition = container.condition as keyof typeof ContainerConditionToChipLabels | undefined;
  const typedMarketStatus = marketStatus as keyof typeof ContainerStatusToChipLabels | undefined;
  const typedStatus = status as keyof typeof ContainerStatusToChipLabels;

  return (
    <ContainerDetailsPaper paperSize="max-content" onClose={onClose}>
      <ContainerDetailsHeader>
        <Typography variant="textL" weight="bold">
          {container.number}
        </Typography>
        <Typography variant="textS">Client: {container.clientYard?.client?.code || '-'}</Typography>
      </ContainerDetailsHeader>
      <ContainerDetail>
        <Typography variant="textS">Type</Typography>
        <Typography variant="textS">{formatContainerType(container.type)}</Typography>
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">Condition</Typography>
        {condition ? (
          <Chip
            size="small"
            label={ContainerConditionToChipLabels[condition]}
            color={ContainerConditionToChipColors[condition]}
          />
        ) : (
          <Typography variant="textS">-</Typography>
        )}
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">Market Status</Typography>
        {typedMarketStatus ? (
          <Chip
            size="small"
            label={ContainerStatusToChipLabels[typedMarketStatus]}
            color={ContainerStatusToChipColors[typedMarketStatus]}
          />
        ) : (
          <Typography variant="textS">-</Typography>
        )}
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">Status</Typography>
        <Chip
          size="small"
          label={ContainerStatusToChipLabels[typedStatus]}
          color={ContainerStatusToChipColors[typedStatus]}
        />
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">Gate In Date</Typography>
        <Typography variant="textS">
          {container.gateIn?.completedAt ? momentTextFormat(container.gateIn.completedAt) : '-'}
        </Typography>
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">Days In Terminal</Typography>
        <Typography variant="textS">
          {momentDiffDays(
            container.gateIn?.completedAt,
            container.gateOut?.completedAt || new Date(),
          )}{' '}
          Days
        </Typography>
      </ContainerDetail>
      <Button size="medium" data-color="destructive" onClick={onDelete}>
        Delete
      </Button>
    </ContainerDetailsPaper>
  );
};

export const ContainersSetLocation = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const yardId = user.yardEmployee?.yardId || '';
  const { data: initialBlocksScene, isFetching } = useYardBlocksSceneQuery([yardId], { yardId });

  return (
    <ContainersSetLocationInner
      yardId={yardId}
      initialBlocksScene={initialBlocksScene}
      isFetching={isFetching}
    />
  );
};

type ContainersSetLocationInnerProps = {
  yardId: string;
  initialBlocksScene: string;
  isFetching: boolean;
};

const ContainersSetLocationInner = ({
  yardId,
  initialBlocksScene,
  isFetching,
}: ContainersSetLocationInnerProps) => {
  const isSmallScreen = useIsSmallScreen();
  const { mutate: editContainer } = useEditContainerMutation();
  const { mutate: removePlaceContainer } = useRemovePlaceContainerMutation();

  const blocksScene = useRef(initialBlocksScene);
  const [updateKey, setUpdateKey] = useState(Math.random());
  const [desktopDefaultSelected, setDesktopDefaultSelected] = useState('');
  const [desktopSelectedContainerFocusKey, setDesktopSelectedContainerFocusKey] = useState('');
  const [desktopSelectedContainerId, setDesktopSelectedContainerId] = useState<string>();
  const [selectedContainerId, setSelectedContainerId] = useState('');
  const [containerNumber, setContainerNumber] = useState('');
  const [block, setBlock] = useState('');
  const [row, setRow] = useState('');
  const [slot, setSlot] = useState('');
  const [tier, setTier] = useState('');
  const [containerLength, setContainerLength] = useState<number>();
  const [initialMobilePlace, setInitialMobilePlace] = useState<MobilePlace>({
    block: '',
    row: '',
    slot: '',
    tier: '',
  });

  useEffect(() => {
    if (isFetching) return;

    blocksScene.current =
      typeof initialBlocksScene === 'string' ? initialBlocksScene : '';
    setUpdateKey(Math.random());
    setDesktopDefaultSelected('');
    setDesktopSelectedContainerFocusKey('');
    setDesktopSelectedContainerId(undefined);
  }, [yardId, isFetching, initialBlocksScene]);

  const handleDesktopContainerPanelChange = (containerId?: string) => {
    setDesktopSelectedContainerId(containerId);
    setDesktopDefaultSelected('');
    setDesktopSelectedContainerFocusKey('');
  };

  const handleSceneContainerSelectFromDropdown = (container: { id: string; number: string }) => {
    setDesktopSelectedContainerId(container.id);
    setDesktopDefaultSelected(container.number);
    setDesktopSelectedContainerFocusKey(`${container.id}-${Date.now()}`);
  };

  const handleClearSceneContainerSelect = () => {
    setDesktopSelectedContainerId(undefined);
    setDesktopDefaultSelected('');
    setDesktopSelectedContainerFocusKey('');
  };

  const resetMobileForm = () => {
    setSelectedContainerId('');
    setContainerNumber('');
    setBlock('');
    setRow('');
    setSlot('');
    setTier('');
    setContainerLength(undefined);
    setInitialMobilePlace({ block: '', row: '', slot: '', tier: '' });
  };

  const handleMobileContainerSelect = (option: any) => {
    if (!option?.id || option.isEmpty) {
      resetMobileForm();
      return;
    }

    if (selectedContainerId === option.id) {
      resetMobileForm();
      return;
    }

    const containerLength = option.type?.startsWith('20') ? 20 : 40;
    const scenePlace = getContainerPlaceFromBlocksScene(blocksScene.current, option.id);

    const nextPlace = {
      block: option.block || scenePlace?.block || '',
      row:
        option.row != null && option.row !== ''
          ? String(option.row)
          : scenePlace?.row || '',
      slot:
        option.slot != null && option.slot !== ''
          ? String(option.slot)
          : scenePlace?.slot || '',
      tier:
        option.tier != null && option.tier !== ''
          ? String(option.tier)
          : scenePlace?.tier || '',
    };

    setSelectedContainerId(option.id);
    setContainerNumber(option.label || option.number || '');
    setBlock(nextPlace.block);
    setRow(nextPlace.row);
    setSlot(nextPlace.slot);
    setTier(nextPlace.tier);
    setContainerLength(containerLength);
    setInitialMobilePlace(nextPlace);
  };

  const handleMobileSave = () => {
    if (!selectedContainerId) return;

    const validationError = validateContainerPlace({
      slotsState: parseSceneToSlotsState(blocksScene.current),
      containerId: selectedContainerId,
      containerLength,
      block,
      row,
      slot,
      previousPlace: initialMobilePlace,
    });

    if (validationError) {
      enqueueSnackbar(validationError, 'warning');
      return;
    }

    const nextTier =
      getNextContainerTier({
        slotsState: parseSceneToSlotsState(blocksScene.current),
        containerId: selectedContainerId,
        containerLength,
        block,
        row,
        slot,
      }) ?? 1;

    editContainer(
      {
        id: selectedContainerId,
        block,
        row: Number(row),
        slot: Number(slot),
        tier: CONTAINER_PLACE_API_TIER,
      },
      {
        onSuccess: () => {
          blocksScene.current = updateContainerPlaceInScene(blocksScene.current, {
            id: selectedContainerId,
            number: containerNumber,
            block,
            row,
            slot,
            tier: String(nextTier),
            length: containerLength,
          });
          setUpdateKey(Math.random());
          setInitialMobilePlace({ block, row, slot, tier: String(nextTier) });
          setTier(String(nextTier));
          enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.SET_LOCATION, 'success');
        },
        onError: () => {
          enqueueSnackbar(MESSAGES.CONTAINERS.ERROR.SET_LOCATION, 'error');
        },
      },
    );
  };

  const handleMobileDelete = () => {
    if (!selectedContainerId) return;

    removePlaceContainer(selectedContainerId, {
      onSuccess: () => {
        blocksScene.current = removeContainerFromScene(blocksScene.current, selectedContainerId);
        setUpdateKey(Math.random());
        resetMobileForm();
        enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.SET_LOCATION, 'success');
      },
      onError: () => {
        enqueueSnackbar(MESSAGES.CONTAINERS.ERROR.SET_LOCATION, 'error');
      },
    });
  };

  const handleDeleteContainer = (containerId: string, rollback: () => void) => {
    removePlaceContainer(containerId, {
      onSuccess: () => {
        enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.SET_LOCATION, 'success');
      },
      onError: () => {
        rollback();
        enqueueSnackbar(MESSAGES.CONTAINERS.ERROR.SET_LOCATION, 'error');
      },
    });
  };

  const handlePasteContainer = (
    containerId: string,
    place: YardPlaceSceneContainerPlace,
    rollback: () => void,
  ) => {
    const slotsState = parseSceneToSlotsState(blocksScene.current);
    const sceneContainer = slotsState.containers.find((item) => item.id === containerId);
    const previousPlace = getContainerPlaceFromBlocksScene(blocksScene.current, containerId);
    const nextPlace = {
      block: place.block,
      row: String(place.row),
      slot: String(place.slot),
    };
    const validationError = validateContainerPlace({
      slotsState,
      containerId,
      containerLength: sceneContainer?.length,
      ...nextPlace,
      previousPlace: previousPlace ?? undefined,
    });

    if (validationError) {
      rollback();
      enqueueSnackbar(validationError, 'warning');
      return;
    }

    const nextTier =
      getNextContainerTier({
        slotsState,
        containerId,
        containerLength: sceneContainer?.length,
        ...nextPlace,
      }) ?? 1;

    editContainer(
      {
        id: containerId,
        block: place.block,
        row: Number(place.row),
        slot: Number(place.slot),
        tier: CONTAINER_PLACE_API_TIER,
      },
      {
        onSuccess: () => {
          if (sceneContainer) {
            blocksScene.current = updateContainerPlaceInScene(blocksScene.current, {
              id: containerId,
              number: sceneContainer.number,
              block: place.block,
              row: String(place.row),
              slot: String(place.slot),
              tier: String(nextTier),
              length: sceneContainer.length,
            });
          }
          enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.SET_LOCATION, 'success');
        },
        onError: () => {
          rollback();
          enqueueSnackbar(MESSAGES.CONTAINERS.ERROR.SET_LOCATION, 'error');
        },
      },
    );
  };

  const Page = isSmallScreen ? SetLocationPageMobile : SetLocationPage;

  return (
    <Page>
      <DashboardHeader id="set-location-header">
        <Typography variant={isSmallScreen ? 'textL' : 'textXL'} weight={'bold'}>
          Set Location
        </Typography>
      </DashboardHeader>
      <YardPlaceScene
        key={updateKey}
        defaultMode={isSmallScreen ? 'view-containers' : 'edit-containers'}
        defaultValue={
          typeof blocksScene.current === 'string' ? blocksScene.current : '{}'
        }
        defaultSelected={isSmallScreen ? containerNumber : desktopDefaultSelected}
        selectedContainerFocusKey={isSmallScreen ? undefined : desktopSelectedContainerFocusKey}
        selectedContainerId={isSmallScreen ? undefined : desktopSelectedContainerId}
        onSelectedContainerChange={
          isSmallScreen ? undefined : handleDesktopContainerPanelChange
        }
        onDeleteContainer={isSmallScreen ? undefined : handleDeleteContainer}
        onPasteContainer={isSmallScreen ? undefined : handlePasteContainer}
        renderOnEmptyClick={
          isSmallScreen
            ? undefined
            : (selectedSlotKey, closeEmptySlot, addContainer, sceneContainers) =>
                !!yardId && (
                  <EmptySlotContainerSelect
                    yardId={yardId}
                    hasSelectedSlot={!!selectedSlotKey}
                    sceneContainers={sceneContainers}
                    onAddContainer={addContainer}
                    onSceneContainerSelect={(container) => {
                      closeEmptySlot?.();
                      handleSceneContainerSelectFromDropdown(container);
                    }}
                    onClearSceneContainerSelect={handleClearSceneContainerSelect}
                  />
                )
        }
        renderOnContainerClick={
          isSmallScreen
            ? undefined
            : (containerId, closeContainerDetails, deleteContainer) =>
                !!containerId &&
                !containerId.startsWith('mock-container-id') && (
                  <ContainerDetails
                    containerId={containerId}
                    onClose={() => closeContainerDetails?.()}
                    onDelete={() => deleteContainer?.()}
                  />
                )
        }
        renderOnSlotHover={
          isSmallScreen
            ? undefined
            : (info) => info && <SlotHoverInfo place={info.place} tier={info.tier} x={info.x} y={info.y} />
        }
        renderOnContainerHover={
          isSmallScreen
            ? undefined
            : (info) =>
                info && (
                  <ContainerHoverInfo
                    number={info.number}
                    type={info.type}
                    clientCode={info.clientCode}
                    place={info.place}
                    tier={info.tier}
                    x={info.x}
                    y={info.y}
                  />
                )
        }
      />
      {isSmallScreen && (
        <MobileSetLocationPanel
          yardId={yardId}
          blocksScene={blocksScene.current}
          selectedContainerId={selectedContainerId}
          containerLength={containerLength}
          containerNumber={containerNumber}
          block={block}
          row={row}
          slot={slot}
          tier={tier}
          initialPlace={initialMobilePlace}
          setBlock={setBlock}
          setRow={setRow}
          setSlot={setSlot}
          setTier={setTier}
          onContainerSelect={handleMobileContainerSelect}
          onSave={handleMobileSave}
          onDelete={handleMobileDelete}
        />
      )}
    </Page>
  );
};
